/**
 * Public header, rendered by MarketingLayoutRoute.
 * @file src/web/shared/components/Header.tsx
 *
 * Plain markup on the brand tokens rather than uikit's <Header tone="brand">.
 * That component paints uikit's own brand colour — blue — which is what these
 * pages were still showing long after the Olito kit landed everywhere else, so
 * /download and /about looked like a different product from the homepage and
 * the console. The chrome here deliberately mirrors the landing page's own
 * header (sticky, real logo, Sign in + Start free) so a client moving from one
 * to the other sees one site.
 */

import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, LayoutDashboard, LogOut } from 'lucide-react';
import { useAuth } from '../../features/auth';
import { brand } from '../brand';

export const Header: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-receipt bg-paper/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <Link to="/" className="no-underline" aria-label="oddits home">
          <img src={brand.logo} alt="oddits" className="h-10 w-auto" />
        </Link>

        <div className="flex items-center gap-1.5">
          {isAuthenticated ? (
            <>
              <Link
                to="/admin"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-inkblack-80 no-underline transition-colors hover:text-inkblack"
              >
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Link>
              <Link
                to="/logout"
                className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-inkblack-80 no-underline transition-colors hover:bg-ink-50"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                className="rounded-full px-4 py-2 text-sm font-semibold text-inkblack-80 no-underline transition-colors hover:text-inkblack"
              >
                Sign in
              </Link>
              {/* Not shown on /download: the visitor there is usually the
                  client installing the agent, not someone signing up. */}
              {pathname !== '/download' && (
                <Link
                  to="/auth/register"
                  className="group inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-paper no-underline shadow-sm transition-all hover:-translate-y-0.5 hover:bg-inkblack-80"
                >
                  Start free
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const PublicHeader = Header;
